import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Monitor, 
  Activity, 
  AlertTriangle, 
  BarChart3,
  ChevronRight,
  Cpu,
  HardDrive,
  Wifi,
  Database,
  Server,
  Clock,
  Eye,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info,
  Zap,
  TrendingUp,
  TrendingDown,
  Search,
  Download,
  Settings
} from 'lucide-react';
import { formatDate } from '../../services/api';

const SystemMonitor = () => {
  const [activeTab, setActiveTab] = useState('performance');
  const [refreshing, setRefreshing] = useState(false);
  const [alertFilter, setAlertFilter] = useState('all');
  const [auditFilter, setAuditFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to generate today's timestamps
  const getTodayTimestamp = (hoursAgo = 0, minutesAgo = 0) => {
    const now = new Date();
    now.setHours(now.getHours() - hoursAgo);
    now.setMinutes(now.getMinutes() - minutesAgo);
    return now.toISOString();
  };

  // Format date with full time including seconds
  const formatDateTimeWithSeconds = (date) => {
    return new Date(date).toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Mock performance data
  const performanceMetrics = {
    cpu: {
      usage: 65,
      trend: 'up',
      status: 'normal',
      history: [45, 52, 48, 61, 58, 65]
    },
    memory: {
      usage: 78,
      trend: 'up',
      status: 'warning',
      history: [65, 68, 72, 75, 76, 78]
    },
    disk: {
      usage: 45,
      trend: 'stable',
      status: 'normal',
      history: [42, 43, 44, 45, 45, 45]
    },
    network: {
      latency: 12, // ms
      trend: 'down',
      status: 'good',
      history: [18, 16, 14, 13, 12, 12]
    }
  };

  const systemServices = [
    {
      name: 'API Gateway',
      status: 'running',
      uptime: '99.98%',
      responseTime: '45ms',
      lastCheck: getTodayTimestamp(0, 0)
    },
    {
      name: 'Database',
      status: 'running',
      uptime: '99.95%',
      responseTime: '8ms',
      lastCheck: getTodayTimestamp(0, 0)
    },
    {
      name: 'RENTAS Integration',
      status: 'na',
      uptime: 'N/A',
      responseTime: 'N/A',
      lastCheck: getTodayTimestamp(0, 0)
    },
    {
      name: 'FAST Integration',
      status: 'na',
      uptime: 'N/A',
      responseTime: 'N/A',
      lastCheck: getTodayTimestamp(0, 0)
    },
    {
      name: 'Email Service',
      status: 'running',
      uptime: '98.45%',
      responseTime: '2.3s',
      lastCheck: getTodayTimestamp(0, 1)
    },
    {
      name: 'Blockchain Node',
      status: 'running',
      uptime: '99.99%',
      responseTime: '350ms',
      lastCheck: getTodayTimestamp(0, 0)
    }
  ];

  // Mock alerts data
  const systemAlerts = [
    {
      id: 1,
      severity: 'critical',
      message: 'High memory usage detected on application server',
      component: 'Application Server',
      timestamp: '2025-07-18T13:36:12.000Z',
      status: 'new',
      details: 'Memory usage has exceeded 85% threshold for 15 minutes'
    },
    {
      id: 2,
      severity: 'high',
      message: 'Email service response time degraded',
      component: 'Email Gateway',
      timestamp: '2025-07-18T12:56:12.000Z',
      status: 'acknowledged',
      details: 'Average response time increased to 2.3 seconds'
    },
    {
      id: 3,
      severity: 'medium',
      message: 'Database connection pool nearing capacity',
      component: 'Database',
      timestamp: '2025-07-18T11:41:12.000Z',
      status: 'resolved',
      details: 'Connection pool utilization reached 90%'
    },
    {
      id: 4,
      severity: 'low',
      message: 'System backup completed successfully',
      component: 'Backup Service',
      timestamp: '2025-07-18T10:26:12.000Z',
      status: 'resolved',
      details: 'Daily automated backup completed without errors'
    }
  ];

  // Mock audit logs
  const auditLogs = [
    {
      id: 1,
      timestamp: '2025-07-18T13:26:12.000Z',
      user: 'admin@facility.com',
      action: 'User Login',
      description: 'Successful login to admin dashboard',
      ipAddress: '203.115.45.67',
      status: 'success'
    },
    {
      id: 2,
      timestamp: '2025-07-18T13:11:12.000Z',
      user: 'system',
      action: 'Automated Backup',
      description: 'Daily database backup completed',
      ipAddress: 'localhost',
      status: 'success'
    },
    {
      id: 3,
      timestamp: '2025-07-18T12:56:12.000Z',
      user: 'user@issuer.com',
      action: 'Failed Login Attempt',
      description: 'Invalid password for user account',
      ipAddress: '125.23.45.67',
      status: 'failure'
    }
  ];

  const tabs = [
    {
      id: 'performance',
      name: 'Performance Metrics',
      icon: Activity,
      description: 'System resource utilization and performance'
    },
    {
      id: 'alerts',
      name: 'Alerts & Errors',
      icon: AlertTriangle,
      description: 'System alerts and error management'
    },
    {
      id: 'audit',
      name: 'Audit Logs',
      icon: Eye,
      description: 'User actions and system events'
    }
  ];

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setRefreshing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
      case 'good':
      case 'normal':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'critical':
      case 'error':
        return 'text-red-600 bg-red-100';
      case 'na':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityBadge = (severity) => {
    const severityConfig = {
      critical: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      high: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertTriangle },
      medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Info },
      low: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info }
    };
    const config = severityConfig[severity] || severityConfig.low;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </span>
    );
  };

  const renderPerformanceTab = () => (
    <div className="space-y-6">
      {/* System Resources */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
                <Cpu className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  CPU Usage
                </p>
                <p className="text-2xl font-bold">
                  {performanceMetrics.cpu.usage}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-purple-100">
                <Database className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Memory Usage
                </p>
                <p className="text-2xl font-bold">
                  {performanceMetrics.memory.usage}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
                <HardDrive className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Disk Usage
                </p>
                <p className="text-2xl font-bold">
                  {performanceMetrics.disk.usage}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-indigo-100">
                <Wifi className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Network Latency
                </p>
                <p className="text-2xl font-bold">
                  {performanceMetrics.network.latency}ms
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Services */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">System Services</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  Service
                </th>
                <th className="table-header-cell">
                  Status
                </th>
                <th className="table-header-cell">
                  Uptime
                </th>
                <th className="table-header-cell">
                  Response Time
                </th>
                <th className="table-header-cell">
                  Last Check
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {systemServices.map((service, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                    
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                      {service.status === 'running' ? <CheckCircle className="w-3 h-3 mr-1" /> : 
                       service.status === 'na' ? <XCircle className="w-3 h-3 mr-1" /> : 
                       <XCircle className="w-3 h-3 mr-1" />}
                      {service.status === 'na' ? 'N/A' : service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {service.uptime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {service.responseTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTimeWithSeconds(service.lastCheck)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAlertsTab = () => {
    const filteredAlerts = systemAlerts.filter(alert => 
      alertFilter === 'all' || alert.severity === alertFilter
    );

    return (
      <div className="space-y-6">
        {/* Alert Filters */}
        <div className="bg-white rounded-lg border border-light-gray p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
            
              <select
                value={alertFilter}
                onChange={(e) => setAlertFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
     
          </div>
        </div>

        {/* Alerts List */}
        <div className="bg-white rounded-lg border-light-gray border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">System Alerts ({filteredAlerts.length})</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredAlerts.map((alert) => (
              <div key={alert.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      {getSeverityBadge(alert.severity)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        alert.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        alert.status === 'acknowledged' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                      </span>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">{alert.message}</h4>
                    <p className="text-sm text-gray-600 mb-2">{alert.details}</p>
                    <div className="flex items-center text-xs text-gray-500 space-x-4">
                      <span>Component: {alert.component}</span>
                      <span>Time: {formatDate(alert.timestamp)}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className="text-primary-400 hover:text-gray-600">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-primary-400 hover:text-gray-600">
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAuditTab = () => {
    const filteredLogs = auditLogs.filter(log => {
      const matchesFilter = auditFilter === 'all' || log.status === auditFilter;
      const matchesSearch = searchTerm === '' || 
        log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });

    return (
      <div className="space-y-6">
        {/* Audit Filters */}
        <div className="bg-white rounded-lg border-light-gray border p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
           
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by user, action, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="sm:w-48">
             
              <select
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </div>
            <div className="flex items-end">
              <button className="btn-primary">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Audit Logs */}
        <div className="bg-white rounded-lg border-light-gray border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Audit Logs ({filteredLogs.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">
                    Timestamp
                  </th>
                  <th className="table-header-cell">
                    User
                  </th>
                  <th className="table-header-cell">
                    Action
                  </th>
                  <th className="table-header-cell">
                    Description
                  </th>
                  <th className="table-header-cell">
                    IP Address
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {typeof log.user === 'string' ? log.user : 'Unknown User'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log.action}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {log.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.ipAddress}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        log.status === 'success' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {log.status === 'success' ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                        {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Monitor</h1>
              <p className="text-sm text-gray-500 mt-1">Monitor system health, performance metrics, and error logs</p>
            </div>
       
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto ">

      {/* Monitoring Tabs */}
      <div className="overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'performance' && renderPerformanceTab()}
          {activeTab === 'alerts' && renderAlertsTab()}
          {activeTab === 'audit' && renderAuditTab()}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SystemMonitor; 